/**
 * Glisser une ligne vers la gauche pour la supprimer — le geste d'une liste
 * iOS native.
 *
 * Demandé après essai sur l'appareil, en remplacement de la croix ronde posée
 * au bout de chaque entrée du journal : sur une ligne de 393 px déjà occupée
 * par un libellé, une quantité et quatre macros, la croix coûtait 44 px de
 * largeur pour un geste rare, et il fallait la viser.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🔴 CE QUI REND CE GESTE DÉLICAT ICI, ET NULLE PART AILLEURS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'écran du Journal porte **déjà** un geste horizontal : le pager entre les
 * journées (`useDayPager`), posé sur `.day-track`, qui englobe toutes les
 * fiches repas. Un glissement sur une ligne d'entrée est donc vu par les deux.
 *
 * D'où `event.stopPropagation()` au `pointerdown` : la piste ne voit jamais le
 * geste, et il n'y a pas d'arbitrage à écrire. ⚠️ Conséquence assumée — on ne
 * change plus de jour en partant d'une ligne d'entrée. C'est le bon compromis :
 * ces lignes n'existent que dans un repas **déplié**, et tout le reste de
 * l'écran reste attrapable.
 *
 * 🔴 **Aucun `setPointerCapture`, contrairement au pager.** La capture
 * retargette les événements de compatibilité — dont **`click`** — sur l'élément
 * capturant : c'est ce qui avait rendu inerte le bouton « Fermer » de toutes
 * les feuilles modales à l'étape 6. Ici l'élément capturant serait la ligne, et
 * le bouton de correction qu'elle contient cesserait de répondre. On s'en passe
 * : la ligne occupe toute la largeur de la carte, le doigt n'en sort donc pas
 * pendant un glissement horizontal, et `pointerleave` / `pointercancel`
 * referment le geste dans les cas restants.
 *
 * ⚠️ **Le verrou d'axe est celui du pager (10 px), délibérément.** Deux seuils
 * différents sur le même écran donneraient deux gestes qui ne « prennent » pas
 * au même moment, ce qui se sent au doigt sans qu'on sache le nommer.
 */

import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

/**
 * Largeur du panneau révélé quand la ligne reste ouverte.
 *
 * 🔴 **124 px, et c'est une mesure.** À 104 px, l'icône de corbeille tombait
 * **hors** de la bande révélée et se retrouvait tranchée par son bord — vu sur
 * l'appareil. Le contenu du panneau fait 16 (glyphe) + 8 (écart) + ~68
 * (« Supprimer ») + 16 (rembourrage) = 108 px : en dessous, quelque chose est
 * coupé.
 *
 * ⚠️ **Valeur en double avec `--swipe-action` dans `styles/nourri-screens.css`**,
 * et le doublon est assumé : le CSS en a besoin pour la position de repos (une
 * classe, donc animable et pilotable au clavier), le JavaScript pour décider où
 * la ligne s'arrête au relâchement. Les faire diverger décale le panneau d'un
 * cran — visible immédiatement, sans conséquence de données. Les deux se citent
 * mutuellement, et **se changent ensemble**.
 */
export const SWIPE_ACTION_PX = 124;

/** Même verrou que `useDayPager` — cf. l'en-tête. */
const AXIS_LOCK_PX = 10;

/** Relâché au-delà : la ligne reste ouverte au lieu de se refermer. */
const OPEN_AT_PX = 44;

/**
 * Glissé au-delà de cette part de la largeur : suppression directe, sans passer
 * par le panneau.
 *
 * ⚠️ **La moitié, pas moins.** C'est un geste **destructeur et non confirmé** :
 * le seuil doit être clairement au-delà de ce qu'un pouce parcourt sans
 * intention. iOS emploie le même ordre de grandeur pour son « full swipe ».
 */
const DELETE_RATIO = 0.5;

interface Gesture {
  pointerId: number;
  x: number;
  y: number;
  axis: 'unknown' | 'x';
  width: number;
  /** Décalage au moment où le doigt s'est posé — 0 fermé, négatif ouvert. */
  base: number;
  moved: boolean;
}

export interface SwipeToDelete {
  /** Décalage courant, en pixels et négatif. `null` = position de repos. */
  offset: number | null;
  dragging: boolean;
  handlers: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => void;
  };
  /**
   * À appeler au tout début d'un `onClick` : rend `true` si le clic n'est que
   * la fin d'un glissement, auquel cas l'appelant doit s'abstenir.
   *
   * ⚠️ Nécessaire parce qu'un `pointerup` après un glissement produit quand
   * même un `click` : sans ce garde-fou, glisser une entrée ouvrirait sa
   * feuille de correction au relâchement.
   */
  consumeTap: () => boolean;
}

export function useSwipeToDelete({
  enabled,
  open,
  onOpen,
  onClose,
  onDelete,
}: {
  /** Faux pour une entrée provisoire : le serveur ne l'a pas encore écrite. */
  enabled: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onDelete: () => void;
}): SwipeToDelete {
  const [offset, setOffset] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const gesture = useRef<Gesture | null>(null);
  const tapBlocked = useRef(false);

  /*
   * ⚠️ **Le repos est `null`, pas un nombre**, et c'est ce qui rend le clavier
   * possible : hors glissement, la position vient d'une **classe** CSS, que
   * `:has(… :focus-visible)` peut donc rouvrir. Un `transform` en ligne aurait
   * gagné contre toute règle de feuille de style, et la ligne serait restée
   * fermée sous le doigt d'un lecteur d'écran.
   *
   * ⚠️ Le retour au repos se fait donc **à la fin de chaque geste**, dans
   * `finish` et `abort`, et non dans un effet qui observerait `open` : un
   * `setState` synchrone dans un effet enchaîne les rendus pour rien, et il n'y
   * a rien à observer — c'est nous qui savons quand le geste s'achève.
   */

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      // 🔴 Le pager de journées ne doit jamais voir ce geste — cf. l'en-tête.
      event.stopPropagation();

      gesture.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        axis: 'unknown',
        width: event.currentTarget.clientWidth,
        base: open ? -SWIPE_ACTION_PX : 0,
        moved: false,
      };
    },
    [enabled, open],
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== event.pointerId) return;

    const dx = event.clientX - g.x;
    const dy = event.clientY - g.y;

    if (g.axis === 'unknown') {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      if (Math.abs(dy) >= Math.abs(dx)) {
        // Défilement vertical : on se retire du geste pour de bon.
        gesture.current = null;
        return;
      }
      g.axis = 'x';
      setDragging(true);
    }

    g.moved = true;
    /*
     * Borné à droite seulement. À gauche on laisse filer jusqu'au bord : c'est
     * ce qui donne au « glissement complet » sa course, et le panneau rouge
     * occupe toute la ligne par construction — il n'y a rien à étirer.
     */
    setOffset(Math.min(0, g.base + dx));
  }, []);

  const finish = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const g = gesture.current;
      gesture.current = null;
      if (!g || g.pointerId !== event.pointerId) return;

      tapBlocked.current = g.moved;
      if (g.axis !== 'x') {
        setDragging(false);
        return;
      }

      const travel = -(offset ?? g.base);
      setDragging(false);
      // Retour au repos : c'est la classe qui décide de la position finale, et
      // la transition CSS qui l'anime.
      setOffset(null);

      if (travel >= g.width * DELETE_RATIO) {
        onDelete();
        return;
      }
      if (travel >= OPEN_AT_PX) onOpen();
      else onClose();
    },
    [offset, onClose, onDelete, onOpen],
  );

  const abort = useCallback(() => {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    tapBlocked.current = g.moved;
    setDragging(false);
    // Un geste interrompu ne décide de rien : on revient à l'état déclaré.
    setOffset(null);
  }, []);

  const consumeTap = useCallback(() => {
    const blocked = tapBlocked.current;
    tapBlocked.current = false;
    return blocked;
  }, []);

  return {
    offset,
    dragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: abort,
      onPointerLeave: abort,
    },
    consumeTap,
  };
}
